import { Coordinate } from 'ol/coordinate';
import { fromLonLat, transform } from 'ol/proj';
import {
  Location,
} from 'src/models';
import { CoordinateTransformOptions, NamedVectorLayer } from '@/lib/OlMapWrapper';
import { LayerEventHandler } from '@/lib/OlMapManager';
import { Extent } from 'ol/extent';
import { Color } from 'ol/color';
import { Style, Stroke, Circle, Icon, Fill, Text } from 'ol/style';
import IconAnchorUnits from 'ol/style/IconAnchorUnits';
import OlStyleCache from '@/lib/OlStyleCache';
import { getBearing } from '@/lib/geoCalcUtil';

export interface DirectionArrowStyleParams {
  zIndex: number;
  rotation: number;
}
export interface CirclePointStyleParams {
  radius: number;
  fillColor: Color;
  strokeColor: Color;
  zIndex: number;
}
export interface CirclePointWithTextStyleParams extends CirclePointStyleParams {
  text: string;
}

export const DIRECTION_POINTER_INTERVAL = 9;
// 車両の矢印の向きを切り捨てる単位。角度ごとの Style を使い回すため
const CAR_ANGLE_ARROW_STEP_DEGREES = 5;

interface CarAngleArrowStyleParams {
  // 北が0・時計回りの度
  angleDegrees: number;
  scale: number;
  // 画像の高さに対する割合。丸の中心から矢印までの距離を決める
  anchorY: number;
  // null なら zIndex を付けない
  zIndex: number | null;
}

const ZOOM_LEVEL_1000M = 13;
// EPSG:3857 の z=0 の解像度 ≒ 156543.03 m/px、z で 1/2 ずつ小さくなる
const RESOLUTION_AT_ZOOM_1000M = 156543.03392804097 / Math.pow(2, ZOOM_LEVEL_1000M);
// zoom > ZOOM_LEVEL_1000M と等価（zoom が上がると resolution は小さくなる）
export const isAboveZoom1000M = (resolution: number): boolean => resolution < RESOLUTION_AT_ZOOM_1000M;

export interface EMLayerInfo {
  onLayerClick?: LayerEventHandler;
  extent: Extent | null;
}
export interface LayerWithInfo {
  layer: NamedVectorLayer | null;
  layerInfo: EMLayerInfo;
}
export default abstract class ExtremeMapAbstractLayerManager {
  layer: NamedVectorLayer | null;
  layerInfo: EMLayerInfo;
  styleCache: OlStyleCache;

  constructor() {
    if (this.constructor === ExtremeMapAbstractLayerManager) {
      throw new TypeError('Abstract class "ExtremeMapAbstractLayerManager" cannot be instantiated directly.');
    }
    this.layer = null;
    this.layerInfo = {
      extent: null,
    };
    this.styleCache = new OlStyleCache();
  }

  isArrayAllSame(a: any[], b: any[]): boolean {
    const aLen = a.length;
    const bLen = b.length;
    if (aLen !== bLen) { return false; }
    a = a.slice().sort();
    b = b.slice().sort();
    for (let i = 0; i < aLen; i++) {
      if (a[i] !== b[i]) { return false; }
    }
    return true;
  }

  coordFromLonLat(lon: number, lat: number, opt: CoordinateTransformOptions = {}): Coordinate {
    const destProj = opt.destProj || 'EPSG:3857';
    return fromLonLat([lon, lat], destProj);
  }

  convCoord({ lat, lon }: Location, opt: CoordinateTransformOptions = {}): Coordinate {
    const srcProj = opt.srcProj || 'EPSG:4326';
    const destProj = opt.destProj || 'EPSG:3857';
    return transform([lon, lat], srcProj, destProj);
  }

  getLayer(): LayerWithInfo {
    const layer = this.layer;
    const layerInfo = this.layerInfo;
    return { layer, layerInfo };
  }

  getLayerVisible(): boolean {
    if (!this.layer) { return false; }
    return this.layer.getVisible();
  }

  toggleLayerVisible(): void {
    if (!this.layer) { return; }
    const visible = this.layer.getVisible();
    this.layer.setVisible(!visible);
  }

  toRadians(angleInDegrees: number): number {
    return angleInDegrees * Math.PI / 180;
  }

  toDegrees(angleInRadians: number): number {
    return angleInRadians * 180 / Math.PI;
  }
  getBearing(pt1: Location, pt2: Location): number {
    return getBearing(pt1, pt2);
  }

  getCirclePointStyle(params: CirclePointStyleParams): Style {
    const { radius, fillColor, strokeColor, zIndex } = params;
    const style = new Style({ zIndex });
    style.setImage(
      new Circle({
        radius,
        fill: new Fill({ color: fillColor }),
        stroke: new Stroke({ color: strokeColor, width: 1 }),
      }),
    );
    return style;
  }

  getCircleWithTextPointStyle(params: CirclePointWithTextStyleParams): Style {
    const { radius, fillColor, strokeColor, zIndex, text } = params;
    const style = new Style({ zIndex });
    style.setImage(
      new Circle({
        radius,
        fill: new Fill({ color: fillColor }),
        stroke: new Stroke({ color: strokeColor, width: 1 }),
      }),
    );

    const font = `${radius * 1.5}px sans-serif`;
    style.setText(
      new Text({
        text,
        font,
        fill: new Fill({ color: 'black' }),
        stroke: new Stroke({
          color: 'rgba(255, 255, 255, 0.8)',
          width: 3,
        }),
        offsetX: 0,
        offsetY: 0,
      }),
    );
    return style;
  }

  getDirectionArrowStyle(params: DirectionArrowStyleParams): Style {
    const { zIndex, rotation = 0 } = params;
    const style = new Style({ zIndex });
    style.setImage(
      new Icon({
        src: '/static/img/arrow-small1.png',
        anchorXUnits: IconAnchorUnits.FRACTION,
        anchorYUnits: IconAnchorUnits.FRACTION,
        scale: 0.35,
        rotation,
      }),
    );
    return style;
  }

  // 車両の丸の外側に重ねる進行方向の矢印。角度は5度単位に切り捨て、大きさ・anchor・角度の組ごとに画像を使い回す
  getCarAngleArrowStyle({ angleDegrees, scale, anchorY, zIndex }: CarAngleArrowStyleParams): Style {
    const alignedAngle = Math.floor(angleDegrees / CAR_ANGLE_ARROW_STEP_DEGREES) * CAR_ANGLE_ARROW_STEP_DEGREES;
    const arrowStyle = this.styleCache.fetch(`carAngleArrow:${scale}:${anchorY}:${alignedAngle}`, () => {
      return new Style({
        image: new Icon({
          src: '/static/img/arrow-small1.png',
          anchor: [0.5, anchorY], // anchorの計算がされてからrotateされる
          anchorXUnits: IconAnchorUnits.FRACTION,
          anchorYUnits: IconAnchorUnits.FRACTION,
          scale,
          rotation: this.toRadians(alignedAngle),
        }),
      });
    });
    if (zIndex === null) return arrowStyle;

    return new Style({ zIndex, image: arrowStyle.getImage() });
  }
}
